import './styles/main.scss';

import { Sidebar } from './nav/Sidebar.js';
import { Notice } from './ui/Notice.js';
import { initAnalytics } from './analytics.js';
initAnalytics();
import { wireWorldPageTransitions } from './nav/wireWorldPageTransitions.js';
import { TransitionManager } from './transitions/TransitionManager.js';
import { runChannelWipe } from './transitions/channelWipe.js';
import { getChannelCover } from './transitions/channelHandoff.js';
import { transitionLog } from './debug/transitionDebug.js';

const EXTERNAL_WORLDS = ['travel'];

/**
 * main-world.js — shared entry for /chotbat/, /travel/, /work/.
 *
 * Deliberately stripped down. World pages do NOT load:
 *   - BackgroundManager (no live video / glitchGL on world pages)
 *   - AudioManager      (audio toggle is decorative on world pages until
 *                        the world's own module wires it up)
 *   - Lenis             (world content modules can opt-in if needed)
 *   - Hero / Carousel   (home-only)
 *
 * They DO load:
 *   - Sidebar + wireWorldPageTransitions (home / Back / #worlds:
 *     TransitionManager.transitionOut → `location.assign`, no shader)
 *   - TransitionManager.init() (bfcache / nav guard only; no WebGL on worlds)
 *
 * FOUC: each world's <head> can set `body { opacity: 0 }` briefly; we set
 * opacity to 1 in boot *before* `TransitionManager.init()`.
 */

async function boot() {
  transitionLog('mainWorld:boot', { world: document.body?.dataset?.world, path: location.pathname });
  new Sidebar();
  new Notice();
  wireWorldPageTransitions();

  // Explicitly load the three critical fonts before revealing. The inline
  // @font-face in each world HTML ensures the browser knows about these fonts
  // even before Vite injects the SCSS, preventing fonts.load() from resolving
  // prematurely with zero pending loads.
  if (document.fonts?.load) {
    try {
      await Promise.all([
        document.fonts.load('400 1em "Anton"'),
        document.fonts.load('400 1em "DM Mono"'),
        document.fonts.load('400 1em "Barlow Condensed"'),
      ]);
    } catch {
      /* ignore — reveal anyway */
    }
  }
  // Three rAF frames ensures the browser has fully resolved layout with
  // loaded fonts and injected CSS before we reveal.
  await new Promise((r) => requestAnimationFrame(() =>
    requestAnimationFrame(() => requestAnimationFrame(r))
  ));

  const world = document.body.dataset.world;
  const cover = getChannelCover();

  // Carry the home page's theme across so the accent doesn't snap.
  if (cover?.dataset.theme) document.body.dataset.theme = cover.dataset.theme;

  // Travel is an iframe: give it a moment to actually have pixels before we
  // tune in to it (the card art holds meanwhile). Hard cap so a slow app
  // never blocks the reveal.
  if (world && EXTERNAL_WORLDS.includes(world)) {
    const frame = document.querySelector('iframe');
    if (frame) {
      await Promise.race([
        new Promise((r) => frame.addEventListener('load', r, { once: true })),
        new Promise((r) => setTimeout(r, 2000)),
      ]);
    }
  }

  // Page is mounted under the cover — make it live, then change the channel.
  document.body.style.opacity = '1';
  await runChannelWipe({
    imageUrl: cover?.dataset.image || null,
    coverLayer: cover,
    zIndex: 2400,   // under the sidebar (3000): the nav stays visible through the channel change
    className: 'world-channel-wipe world-channel-wipe--entry',
  });

  transitionLog('mainWorld:init_transition', { world: document.body?.dataset?.world });
  void TransitionManager.init().then(() => {
    transitionLog('mainWorld:tm_init_resolved', { world: document.body?.dataset?.world });
  });

  // World-specific module dispatch.
  if (world) {
    loadWorldModule(world).catch((err) => {
      console.warn(`[main-world] failed to load world="${world}":`, err);
    });
  }
}

/**
 * World module loader. Stubs for now — when each world's module ships,
 * dynamic-import it here so Vite code-splits per world.
 */
async function loadWorldModule(name) {
  switch (name) {
    case 'chotbat': {
      const { default: ChotbatWorld } = await import('./worlds/chotbat/chotbat.js');
      const chotbat = new ChotbatWorld();
      chotbat.init();
      return;
    }
    case 'mild-concern': {
      const container = document.getElementById('top');
      if (!container) return;
      const { default: TbdWorld } = await import('./worlds/tbd/tbd.js');
      const tbd = new TbdWorld(container);
      tbd.init();
      return;
    }
    case 'work': {
      const host = document.getElementById('top');
      if (!host) return;
      const { default: WorkWorld } = await import('./worlds/work/work.js');
      host.innerHTML = '<div class="world-overlay__content" id="worldContent"></div>';
      new WorkWorld(host.firstElementChild).init();
      return;
    }
    case 'travel':
      console.info(`[main-world] world="${name}" — module not yet implemented`);
      return;
    default:
      console.warn(`[main-world] unknown world="${name}"`);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
